/**
 * Client status derivation utility
 *
 * Status priority order (highest to lowest):
 * 1. INACTIVE - contract has ended
 * 2. EXPIRED_KEY - API key has expired
 * 3. INVALID_KEY - Drata refused the key while its expiration date is still in
 *    the future
 * 4. KEY_EXPIRING_SOON - API key expires within 7 days
 * 5. MISSING_KEY - no API key configured
 * 6. ACTIVE - all good
 */

export const CLIENT_STATUSES = [
  'ACTIVE',
  'INACTIVE',
  'EXPIRED_KEY',
  'INVALID_KEY',
  'KEY_EXPIRING_SOON',
  'MISSING_KEY',
] as const

export type ClientStatus = (typeof CLIENT_STATUSES)[number]

export const CLIENT_STATUS_LABELS: Record<ClientStatus, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  EXPIRED_KEY: 'Expired Key',
  INVALID_KEY: 'Invalid Key',
  KEY_EXPIRING_SOON: 'Key Expiring Soon',
  MISSING_KEY: 'Missing Key',
}

/** Threshold for "key expiring soon" warning (matches Slack notification threshold) */
export const KEY_EXPIRING_SOON_DAYS = 7

export interface ClientStatusInput {
  contractEnd: string | null
  keyExpiration: string | null
  keyStatus: 'valid' | 'invalid' | null
  hasKey: boolean
}

/**
 * Derives the client status from contract and key information.
 * Returns the highest priority status that applies.
 */
export function deriveClientStatus(client: ClientStatusInput): ClientStatus {
  const now = new Date()

  // Priority 1: INACTIVE (contract ended)
  if (client.contractEnd && new Date(client.contractEnd) < now) {
    return 'INACTIVE'
  }

  // Priority 2: EXPIRED_KEY
  if (client.keyExpiration && new Date(client.keyExpiration) < now) {
    return 'EXPIRED_KEY'
  }

  // Priority 3: INVALID_KEY (Drata refused it, whatever the stored dates say)
  if (client.keyStatus === 'invalid') {
    return 'INVALID_KEY'
  }

  // Priority 4: KEY_EXPIRING_SOON (within 7 days)
  if (client.keyExpiration) {
    const expiration = new Date(client.keyExpiration)
    const daysUntilExpiration = Math.ceil((expiration.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    if (daysUntilExpiration <= KEY_EXPIRING_SOON_DAYS && daysUntilExpiration >= 0) {
      return 'KEY_EXPIRING_SOON'
    }
  }

  // Priority 5: MISSING_KEY
  if (!client.hasKey) {
    return 'MISSING_KEY'
  }

  // Priority 6: ACTIVE (default)
  return 'ACTIVE'
}
